import React, { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import { logger } from '../../lib/logger';
import type { MapBounds } from '../../types';
import { useQueryClient } from '@tanstack/react-query';
import { hasUserInteractedWithMapAtom } from '../../atoms/map';
import { useSetAtom } from 'jotai';

interface BoundsUpdaterProps {
  onPositionChange: (center: [number, number], zoom: number) => void;
  onBoundsChange: (bounds: MapBounds) => void;
}

export default function BoundsUpdater({ onPositionChange, onBoundsChange }: BoundsUpdaterProps) {
  const map = useMap();
  const queryClient = useQueryClient();
  const timeoutRef = useRef<NodeJS.Timeout>();
  const lastBoundsRef = useRef<string>('');
  const lastUpdateRef = useRef<number>(0);
  const isDraggingRef = useRef<boolean>(false);
  const MIN_UPDATE_INTERVAL = 500; // Increase to 500ms to reduce updates
  const DRAG_THROTTLE = 1000; // Even longer throttle during drag
  
  const setHasUserInteractedWithMap = useSetAtom(hasUserInteractedWithMapAtom);

  useEffect(() => {
    if (!map) return;

    const handleMapMove = () => {
      const now = Date.now();
      const minInterval = isDraggingRef.current ? DRAG_THROTTLE : MIN_UPDATE_INTERVAL;
      
      // Skip if updating too frequently
      if (now - lastUpdateRef.current < minInterval) {
        // Debounce further updates
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        
        timeoutRef.current = setTimeout(handleMapMove, minInterval);
        return;
      }

      try {
        // Check if map is valid and has methods we need
        if (!map || !map.getBounds || typeof map.getBounds !== 'function') {
          return;
        }
        
        // Check if the map container is still in the DOM
        if (!map._container || !document.body.contains(map._container)) {
          return;
        }
        
        const bounds = map.getBounds();
        const north = bounds.getNorth();
        const south = bounds.getSouth();
        const east = bounds.getEast();
        const west = bounds.getWest();

        const newBounds = { north, south, east, west };

        // Create a string representation of bounds with less precision
        // Reducing precision helps avoid micro-movements triggering updates
        const boundsKey = `${north.toFixed(4)},${south.toFixed(4)},${east.toFixed(4)},${west.toFixed(4)}`;
        
        // Only update if bounds have significantly changed
        if (boundsKey !== lastBoundsRef.current) {
          lastBoundsRef.current = boundsKey;
          lastUpdateRef.current = now;
          
          logger.info('Map moved, new bounds:', 'BoundsUpdater', { 
            bounds: newBounds,
            zoom: map.getZoom(),
            latDiff: north - south,
            lngDiff: east - west
          });
          
          onBoundsChange(newBounds);
          const center = map.getCenter();
          onPositionChange([center.lat, center.lng], map.getZoom());
          
          // Use a longer staleTime to prevent flickering
          queryClient.invalidateQueries({ 
            queryKey: ['incidents'],
            // Only refetch if the data is really stale
            refetchType: "active",
          });
        }
      } catch (error) {
        logger.error(error instanceof Error ? error : new Error('Failed to update bounds'), 'updateMapBounds');
      }
    };

    // Track dragging state to reduce updates during drag
    const handleDragStart = () => {
      isDraggingRef.current = true;
      // User has started interacting with the map
      setHasUserInteractedWithMap(true);
    };
    
    const handleDragEnd = () => {
      isDraggingRef.current = false;
      // Force an update when drag ends
      handleMapMove();
      // User has interacted with the map
      setHasUserInteractedWithMap(true);
    };

    const handleZoomEnd = () => {
      // User has interacted with the map by zooming
      setHasUserInteractedWithMap(true);
    };

    // Use a try-catch block when setting up event listeners
    try {
      map.on('moveend', handleMapMove);
      map.on('zoomend', handleZoomEnd); // Add zoomend listener
      map.on('dragstart', handleDragStart);
      map.on('dragend', handleDragEnd);
      
      // Trigger initial bounds update with a delay to ensure map is ready
      const initialTimeout = setTimeout(() => {
        if (map && document.body.contains(map._container)) {
          handleMapMove();
        }
      }, 500);
      
      return () => {
        clearTimeout(initialTimeout);
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        
        // Safely remove event listeners
        try {
          map.off('moveend', handleMapMove);
          map.off('zoomend', handleZoomEnd); // Remove zoomend listener
          map.off('dragstart', handleDragStart);
          map.off('dragend', handleDragEnd);
        } catch (e) {
          // Ignore errors during cleanup
        }
      };
    } catch (error) {
      logger.error(error instanceof Error ? error : new Error('Failed to set up map event listeners'), 'BoundsUpdater');
      return () => {};
    }
  }, [map, onBoundsChange, onPositionChange, queryClient, setHasUserInteractedWithMap]); // Add setHasUserInteractedWithMap to dependencies

  return null;
}